
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { fetcher } from 'api/client';
import { useForm } from 'react-hook-form';
import { getMilestone, addMilestoneAttachment, markMilestoneAsCompleted, removeMilestoneAttachment } from 'api/actions/milestones';
import { getSession } from 'api/actions/auth'
import DragZone from 'components/Shared/DragZone';
import { parseDate } from 'utils';
import bootbox from 'components/Modals/Bootbox';
import { uploadFileV2 } from 'api/actions/file';
import { Card, Container, Row, Button, FormGroup } from 'reactstrap';
import { useParams } from 'react-router-dom';

function Milestones() {
    const { t, i18n } = useTranslation();
    const language = useRef();
    const queryClient = useQueryClient();
    const { milestoneId, contractId, uuid } = useParams();
    const [jwt, setJWT] = useState();
    const hasMilestone = milestoneId && contractId;
    const { register, handleSubmit } = useForm();

    const { data: session = {} } = useQuery(['fetch-session'], () => fetcher(getSession({ uuid, contractId })), {
        enabled: !!uuid,
        onSuccess: (data) => {
            setJWT(data.jwt)
        }
    })

    const onSuccess = () => {
        queryClient.invalidateQueries(['contract-milestone', contractId]);
    }

    useEffect(() => {
        language.current.value = i18n.language;
    }, [i18n])

    const { data: milestone = {}, isSuccess } = useQuery(['contract-milestone', contractId], () => fetcher(getMilestone({ contractId, milestoneId }), jwt), { enabled: !!(hasMilestone && session.jwt) })

    const { mutate: mutateAsCompleted } = useMutation((payload) => fetcher(markMilestoneAsCompleted(payload), jwt), {
        onSuccess
    })

    const { mutate: uploadMutation, isLoading } = useMutation((payload) => fetcher(uploadFileV2(payload), jwt))

    const { mutate: addMilestoneAttachmentMutation } = useMutation((payload) => fetcher(addMilestoneAttachment(payload), jwt), {
        onSuccess
    })

    const { mutate: removeMilestoneAttachmentMutation } = useMutation((payload) => fetcher(removeMilestoneAttachment(payload), jwt), {
        onSuccess
    })

    const handleForm = (payload) => {

    }

    const markAsCompleted = async () => {
        if (await bootbox.confirm(t('milestones.confirm_complete'))) {
            mutateAsCompleted({
                contractId,
                milestoneId,
            })
        }
    }

    const handleDeleteAttachment = async (attachmentId) => {
        if (await bootbox.confirm(t('milestones.confirm_delete'))) {
            removeMilestoneAttachmentMutation({
                contractId,
                milestoneId,
                attachmentId
            })
        }
    }

    const handleUpload = (files) => {
        files.forEach(async (file) => {
            uploadMutation(file, {
                onSuccess: (uploadResult) => {
                    const data = {
                        filename: uploadResult.BaseFileName,
                        contractId,
                        milestoneId,
                        name: uploadResult.BaseFileName,
                        fileSize: uploadResult.fileSize,
                        url: uploadResult.url,
                        fileId: uploadResult.fileId
                    }
                    addMilestoneAttachmentMutation(data)
                }
            });
            
        })
    }

    const handleLangChange = (e) => {
        i18n.changeLanguage(e.target.value)
    }

    return (
        <div className="">
            <FormGroup className="p-2 float-right">
                <select onChange={handleLangChange} className="form-control" ref={language}>
                    <option value="en" >{t('profile.settings.lang_en')}</option>
                    <option value="es" >{t('profile.settings.lang_es')}</option>
                    <option value="pt" >{t('profile.settings.lang_pt')}</option>
                    <option value="de" >{t('profile.settings.lang_de')}</option>
                </select>
            </FormGroup>
            <Container className="p-5">
                <Card className="p-5">
                    <img src="/images/owl-logo.svg" className="w-10 mb-3" alt="Trato Logo" height="100px" />
                    {isSuccess &&
                        <form onSubmit={handleSubmit(handleForm)}>
                            <h4 className="mb-4"><b>{t('milestones.milestone_info')}</b></h4>
                            <p><b>{t('milestones.name')}:</b> {milestone.name}</p>
                            <p><b>{t('milestones.responsible')}:</b> {milestone.responsible?.email}</p>
                            <p><b>{t('milestones.expected_date')}:</b> {parseDate(milestone.expectedDate, 'PP')}</p>
                            {milestone.realDate && <p><b>{t('milestones.real_date')}:</b> {parseDate(milestone.realDate, 'PP')}</p>}

                            {isLoading &&
                                <div className="mt-1 mb-1 mr-1 align-items-center justify-content rounded p-3 bg-grey file-container pointer">
                                    <i className="fas fa-spinner fa-spin fa-2x background-orange p-2"></i>
                                    <div className="d-flex flex-column ml-3">
                                        <span>{t('milestones.loading')}...</span>
                                    </div>
                                </div>
                            }

                            {milestone.attachments?.map((a) => (
                                <div key={a._id}>
                                    {!milestone.completed && <div className="float-right pointer remove-attachment" onClick={() => handleDeleteAttachment(a._id)}><i className="fas fa-times"></i></div>}
                                    <div className="mt-1 mb-1 mr-1 align-items-center justify-content rounded p-3 bg-grey file-container pointer" onClick={() => window.open(a.url, '_blank')}>
                                        <i className="fas fa-file fa-2x background-orange p-2"></i>
                                        <div className="d-flex flex-column ml-3">
                                            <span>{a.name}</span>
                                            <span className="text-muted">{parseDate(a.createdAt, 'PPp')}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <span></span>
                            {/* <button className="btn btn-default btn-block mt-2" type="submit">Guardar</button> */}
                            {milestone.completed ? <h4 className="mt-4 text-center text-success"><b>{t('milestones.completed')}</b></h4> :
                                <>
                                    <DragZone
                                        multiple={true}
                                        imgSrc="/images/listing/createDocument/subir.svg"
                                        label={t('milestones.upload_evidences')}
                                        className="frontdesk-drag mt-2"
                                        // acceptedFiles={fileTypes}
                                        handleFile={handleUpload}
                                        onDrop={handleUpload}
                                    />
                                    <Button className="mt-2" block color="primary" onClick={markAsCompleted}>{t('milestones.mark_as_completed')}</Button>

                                </>}
                        </form>
                    }
                </Card>
            </Container>
        </div >
    )
}

export default Milestones;
